// 判断是否在飞书客户端内
export const isInFeishuClient = () => {
  return /Lark|Feishu/i.test(navigator.userAgent);
};

const feishuAppId = 'cli_aa9fb860923adbd9';
const feishuRedirectUri = `${window.location.origin}/auth/callback`;
const feishuOAuthStateKey = 'feishu_oauth_state';
const feishuReturnToKey = 'feishu_return_to';
const feishuLoginRetryKey = 'feishu_login_retry_count';

export const getCurrentFullPath = () => {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const getLoginFromPath = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('from');
};

export const normalizeFeishuReturnTo = (returnTo?: string | null) => {
  if (!returnTo) {
    return '/';
  }

  try {
    const url = new URL(returnTo, window.location.origin);
    if (url.origin !== window.location.origin) {
      return '/';
    }

    const fullPath = `${url.pathname}${url.search}${url.hash}`;
    if (['/login', '/auth/callback'].includes(url.pathname)) {
      return '/';
    }

    return fullPath || '/';
  } catch (error) {
    return '/';
  }
};

export const getFeishuReturnTo = () => {
  return sessionStorage.getItem(feishuReturnToKey);
};

export const clearFeishuReturnTo = () => {
  sessionStorage.removeItem(feishuReturnToKey);
  sessionStorage.removeItem(feishuOAuthStateKey);
};

export const getFeishuLoginRetryCount = () => {
  const retry = Number(sessionStorage.getItem(feishuLoginRetryKey));

  if (!Number.isFinite(retry) || retry < 0) {
    return 0;
  }

  return Math.floor(retry);
};

export const increaseFeishuLoginRetryCount = () => {
  const retry = getFeishuLoginRetryCount() + 1;
  sessionStorage.setItem(feishuLoginRetryKey, retry.toString());
  return retry;
};

export const clearFeishuLoginRetryCount = () => {
  sessionStorage.removeItem(feishuLoginRetryKey);
};

export const getFeishuOAuthState = () => {
  return sessionStorage.getItem(feishuOAuthStateKey);
};

export const saveFeishuReturnTo = (returnTo: string) => {
  sessionStorage.setItem(feishuReturnToKey, normalizeFeishuReturnTo(returnTo));
};

// 初始化飞书JSAPI
export const initFeishuSDK = async () => {
  if (!window.h5sdk) {
    // 动态引入JSAPI（推荐方式）
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      // https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.44.js
      script.src =
        'https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.44.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  const sdk = window.h5sdk;
  if (!sdk) {
    throw new Error('飞书JSAPI加载失败');
  }
  return sdk;
};

// 获取飞书客户端内免登授权码
export const getFeishuAuthCode = async (): Promise<string> => {
  const h5sdk = await initFeishuSDK();

  return new Promise((resolve, reject) => {
    h5sdk.ready(() => {
      const tt = window.tt;
      if (!tt?.requestAccess) {
        reject(new Error('飞书 requestAccess 不可用'));
        return;
      }

      tt.requestAccess({
        // 网页应用 App ID
        appID: feishuAppId,
        scopeList: [],
        success: (res) => {
          // 用户授权后返回预授权码
          const { code } = res;
          resolve(code as string);
          // const { data: token } = await requestFeishuLogin({ code: code as string });
          // resolve(token);
        },
        fail: (error) => {
          // 需要额外根据errno判断是否为 客户端不支持requestAccess导致的失败
          const { errno } = error;
          if (errno === 103) {
            if (!tt.requestAuthCode) {
              reject(new Error('飞书 requestAuthCode 不可用'));
              return;
            }

            // 客户端版本过低，不支持requestAccess，需要改为调用requestAuthCode
            tt.requestAuthCode({
              // 网页应用 App ID
              appId: feishuAppId,
              success: (res) => {
                // 用户免登录后返回预授权码
                resolve(res.code as string);

                // const { data: token } = await requestFeishuLogin({ code: res.code as string });
                // resolve(token);
              },
              fail: (error) => {
                // 免登失败，返回相应的errno和errString
                reject(error);
              },
            });
          } else {
            // 用户拒绝授权或者授权失败
            reject(error);
          }
        },
      });
    });
  });
};

// 构造浏览器端飞书OAuth授权链接
export const buildFeishuOAuthUrl = (returnTo = getCurrentFullPath()) => {
  const baseUrl = 'https://open.feishu.cn/open-apis/authen/v1/index';
  const state = Math.random().toString(36).substring(7);
  const safeReturnTo = normalizeFeishuReturnTo(returnTo);

  saveFeishuReturnTo(safeReturnTo);
  sessionStorage.setItem(feishuOAuthStateKey, state);

  const params = new URLSearchParams({
    app_id: feishuAppId,
    redirect_uri: feishuRedirectUri,
    response_type: 'code',
    scope: 'contact:user.base:readonly auth:user.id:readonly',
    // state参数用于防止CSRF攻击，建议生成随机字符串
    state,
    // 可选：强制用户重新授权
    // prompt: 'consent'
  });

  return `${baseUrl}?${params.toString()}`;
};
